import React, { useState, useCallback, useEffect } from 'react';
import { Button, Input, Row, Col, Typography, Checkbox, notification, Modal, Spin } from 'antd';
import { ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import AgGridTable from '../common/AgGridTable';
import moment from 'moment';
import { connect } from 'react-redux';
import { fetchEquipmentUsageLog, updateEquipmentUsageLog, updateDsrLogSections } from '../../redux/dsr/action';

const { Title, Text } = Typography;
const { TextArea } = Input;

// New sample data including all columns from both image sets
const initialRowData = [
    { id: 1, equipment: 'Tesi Deutz 120kW', serialNumber: 'MG/D/373', status: 'ONLINE', hsCode: 'Test 1', spareAvail: 'NO', type: 'Test type', issues: 'Old date' },
    { id: 2, equipment: 'Barrel Roller (Tumbling Cage)', serialNumber: 'BR/033', status: 'DEMOBILIZE', hsCode: 'Test', spareAvail: 'YES', type: 'Check', issues: '' },
    { id: 3, equipment: '140 CFM Compressor', serialNumber: 'MC/140/015', status: 'OFFLINE', hsCode: '', spareAvail: null, type: '', issues: '' },
    { id: 4, equipment: 'BRS 3 Outlet', serialNumber: 'BRS/003', status: 'BACKUP', hsCode: '', spareAvail: null, type: '', issues: '' },
];

const EquipmentUsageSection = ({
    shift,
    selectedDate,
    selectedProject,
    selectedMainline,
    currentDsrLog,
    isSkipped,
    isSubmitted,
    onComplete,
    onCancel,
    isShiftSkipped = false,
    onSkipChange,
    // Redux props
    onFetchEquipmentUsageLog,
    onUpdateEquipmentUsageLog,
    onUpdateDsrLogSections,
    isEquipmentUsageLogLoading,
    isUpdatingEquipmentUsageLog,
    currentEquipmentUsageLog
}) => {
    const [rowData, setRowData] = useState([]);
    const [comments, setComments] = useState('');
    const [skipped, setSkipped] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    // Format the selected date or use today's date as fallback
    const displayDate = selectedDate ? moment(selectedDate).format('DD-MMM-YYYY') : moment().format('DD-MMM-YYYY');

    // Convert shift string to number for API
    const getShiftNumber = (shiftStr) => {
        return shiftStr === 'day' ? 0 : 1;
    };

    // Convert status number to string
    const getStatusFromNumber = (status) => {
        switch (status) {
            case 1: return 'OFFLINE';
            case 2: return 'BACKUP';
            case 3: return 'ONLINE';
            default: return 'Select Status';
        }
    };

    // Convert status string to number
    const getStatusToNumber = (status) => {
        switch (status) {
            case 'OFFLINE': return 1;
            case 'BACKUP': return 2;
            case 'ONLINE': return 3;
            default: return 'Select Status';
        }
    };

    // Convert spare available number to string
    const getSpareAvailableFromNumber = (spareAvailable) => {
        switch (spareAvailable) {
            case 1: return 'YES';
            case 2: return 'NO';
            default: return 'Select';
        }
    };

    // Convert spare available string to number
    const getSpareAvailableToNumber = (spareAvailable) => {
        switch (spareAvailable) {
            case 'YES': return 1;
            case 'NO': return 2;
            default: return null;
        }
    };

    // Convert API data to table format
    const convertApiDataToTableFormat = (apiData) => {
        if (!apiData || !apiData.data || !apiData.data.data) {
            return [];
        }

        const convertedData = apiData.data.data.map((item, index) => ({
            id: item.id,
            pihEquipmentId: item.pihEquipmentId,
            equipment: item.pihEquipmentType || item.equipmentName || '-',
            serialNumber: item.serialNumber || '-',
            status: getStatusFromNumber(item.status),
            hsCode: item.hsCode || '',
            spareAvail: getSpareAvailableFromNumber(item.spareAvailable),
            type: item.type || '',
            issues: item.issues || '',
        // Store original API data for updates
            apiData: item
        }));

        return convertedData;
    };

    // Fetch equipment usage log when component mounts or dependencies change
    useEffect(() => {
        if (selectedProject && selectedMainline && selectedDate) {
            const userEmail = JSON.parse(localStorage.getItem('user')).email;
            const logDate = moment(selectedDate).format('YYYY-MM-DD');
            const shiftNumber = getShiftNumber(shift);

            onFetchEquipmentUsageLog({
                projectId: selectedProject.id,
                logDate: logDate,
                userEmail: userEmail,
                shift: shiftNumber,
                sowId: selectedMainline.id
            });
        }
    }, [selectedProject, selectedMainline, selectedDate, shift, onFetchEquipmentUsageLog]);

    // Update local state when API data is received
    useEffect(() => {
        if (currentEquipmentUsageLog) {
            const convertedData = convertApiDataToTableFormat(currentEquipmentUsageLog);
            setRowData(convertedData);
            setIsDirty(false);
        }
    }, [currentEquipmentUsageLog]);

    // Update skip state from props
    useEffect(() => {
        setSkipped(isSkipped || false);
    }, [isSkipped]);

    // Reset dirty state when date changes (new data should be loaded)
    useEffect(() => {
        setIsDirty(false);
    }, [selectedDate]);

    // Reset skip state when section or shift changes
    useEffect(() => {
        setSkipped(isSkipped || false);
    }, [isSkipped, shift, selectedDate]);

    // Clear local state when component unmounts
    useEffect(() => {
        return () => {
            setRowData([]);
            setComments('');
            setSkipped(false);
            setIsDirty(false);
        };
    }, []);

    const columnDefs = [
        { headerName: 'Equipment', field: 'equipment', editable: false, flex: 1 },
        { headerName: 'Serial Number', field: 'serialNumber', editable: false, flex: 1 },
        {
            headerName: 'Status',
            field: 'status',
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: {
                values: ['Select Status', 'OFFLINE', 'BACKUP', 'ONLINE'],
            },
            flex: 1,
            suppressClickEdit: false,
            singleClickEdit: true,
            cellStyle: params => {
                if (params.colDef.editable && !skipped && !isSubmitted) {
                    return {
                        backgroundColor: 'rgb(50, 29, 30)',
                        borderRadius: '8px',
                        border: '1px solid #666'
                    };
                }
                return null;
            }
        },
        {
            headerName: 'HS Code',
            field: 'hsCode',
            editable: true,
            flex: 1,
            suppressClickEdit: false,
            singleClickEdit: true,
            cellStyle: params => {
                if (params.colDef.editable && !skipped && !isSubmitted) {
                    return {
                        backgroundColor: 'rgb(50, 29, 30)',
                        borderRadius: '8px',
                        border: '1px solid #666'
                    };
                }
                return null;
            }
        },
        {
            headerName: 'Spare Avail.(Y/N)',
            field: 'spareAvail',
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: {
                values: ['Select', 'YES', 'NO'],
            },
            flex: 1,
            suppressClickEdit: false,
            singleClickEdit: true,
            cellStyle: params => {
                if (params.colDef.editable && !skipped && !isSubmitted) {
                    return {
                        backgroundColor: 'rgb(50, 29, 30)',
                        borderRadius: '8px',
                        border: '1px solid #666'
                    };
                }
                return null;
            }
        },
        {
            headerName: 'Type',
            field: 'type',
            editable: true,
            flex: 1,
            suppressClickEdit: false,
            singleClickEdit: true,
            cellStyle: params => {
                if (params.colDef.editable && !skipped && !isSubmitted) {
                    return {
                        backgroundColor: 'rgb(50, 29, 30)',
                        borderRadius: '8px',
                        border: '1px solid #666'
                    };
                }
                return null;
            }
        },
        {
            headerName: 'Issues',
            field: 'issues',
            editable: true,
            flex: 1,
            suppressClickEdit: false,
            singleClickEdit: true,
            cellStyle: params => {
                if (params.colDef.editable && !skipped && !isSubmitted) {
                    return {
                        backgroundColor: 'rgb(50, 29, 30)',
                        borderRadius: '8px',
                        border: '1px solid #666'
                    };
                }
                return null;
            }
        },
    ];

    const onCellValueChanged = useCallback((params) => {
        console.log('Cell value changed:', params.data);
        console.log('Field changed:', params.colDef.field, 'New value:', params.newValue);
        setIsDirty(true);

        // Update the local rowData with the new values
        setRowData(prevData => {
            const updatedData = prevData.map(row =>
                row.id === params.data.id ? { ...row, ...params.data } : row
            );
            console.log('Updated rowData:', updatedData);
            return updatedData;
        });
    }, []);

    const handleCommentsChange = (e) => {
        setComments(e.target.value);
        setIsDirty(true);
    };

    const handleSkipChange = (e) => {
        const newSkipState = e.target.checked;
        setSkipped(newSkipState);
        setIsDirty(true);

        // Use the parent component's centralized skip handler
        if (onSkipChange) {
            onSkipChange(e);
        } else {
            // Fallback to local handling if no parent handler provided
            if (currentDsrLog?.id) {
                const submissionStatus = generateSubmissionStatus('equipment', shift, true);
                const sectionsSkipped = {
                    ...currentDsrLog.sectionsSkipped,
                    isEquipmentUsageSkippedDayShift: shift === 'day' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isEquipmentUsageSkippedDayShift || 0),
                    isEquipmentUsageSkippedNightShift: shift === 'night' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isEquipmentUsageSkippedNightShift || 0),
                };

                onUpdateDsrLogSections({
                    dsrLogId: currentDsrLog.id,
                    submissionStatus: submissionStatus,
                    sectionsSkipped: sectionsSkipped
                });
            }
        }
    };

    // Helper function to generate submission status fields for a specific section and shift
    const generateSubmissionStatus = (section, shift, isSubmitted = true) => {
        const shiftKeySuffix = shift === 'day' ? 'Dayshift' : 'Nightshift';
        let submittedKey;

        switch (section) {
            case 'technician':
                submittedKey = `isTechnicianLogSubmitted${shiftKeySuffix}`;
                break;
            case 'downtime':
                submittedKey = `isDowntimeSubmitted${shiftKeySuffix}`;
                break;
            case 'consumables':
                submittedKey = `isConsumablesSubmitted${shiftKeySuffix}`;
                break;
            case 'equipment':
                submittedKey = `isEquipmentUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'ehs':
                submittedKey = `isEhsIssuesSubmitted${shiftKeySuffix}`;
                break;
            case 'material':
                submittedKey = `isMaterialUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'production':
                submittedKey = `isProductionJointSubmitted${shiftKeySuffix}`;
                break;
            default:
                return {};
        }

        return { [submittedKey]: isSubmitted };
    };

    const handleSave = async () => {
        if (skipped) {
            // If skipped, just complete without saving data to API
            if (onComplete) onComplete(true, 'equipment');
            return;
        }

        try {
        // Prepare data for API
            const dsrLogEquipmentUsage = rowData.map(row => ({
                hsCode: row.hsCode || null,
                status: getStatusToNumber(row.status),
                type: row.type || null,
                spareAvailable: getSpareAvailableToNumber(row.spareAvail),
                issues: row.issues || null,
                pihEquipmentId: row.pihEquipmentId,
                dsrLogId: currentDsrLog?.id,
                shift: getShiftNumber(shift)
            }));

            const payload = {
                dsrLogEquipmentUsage
            };

            await onUpdateEquipmentUsageLog(payload);
            setIsDirty(false);

            // Update submission status and sectionsSkipped for this section
            const submissionStatus = generateSubmissionStatus('equipment', shift, true);
            const sectionsSkipped = {
                ...currentDsrLog.sectionsSkipped,
                isEquipmentUsageSkippedDayShift: shift === 'day' ? 0 : (currentDsrLog.sectionsSkipped?.isEquipmentUsageSkippedDayShift || 0),
                isEquipmentUsageSkippedNightShift: shift === 'night' ? 0 : (currentDsrLog.sectionsSkipped?.isEquipmentUsageSkippedNightShift || 0)
            };

            await onUpdateDsrLogSections({
                dsrLogId: currentDsrLog?.id,
                submissionStatus: submissionStatus,
                sectionsSkipped: sectionsSkipped
            });

            // Only navigate to next section on successful save
            if (onComplete) onComplete(true, 'equipment');

            // Refresh data after successful save
            if (selectedProject && selectedMainline && selectedDate) {
                const userEmail = JSON.parse(localStorage.getItem('user')).email;
                const logDate = moment(selectedDate).format('YYYY-MM-DD');
                const shiftNumber = shift === 'day' ? '0' : '1';

                onFetchEquipmentUsageLog({
                    projectId: selectedProject.id,
                    logDate: logDate,
                    userEmail: userEmail,
                    shift: shiftNumber,
                    sowId: selectedMainline.id
                });
            }

        } catch (error) {
            console.error('Error saving equipment usage log:', error);
            notification.error({
                message: 'Save Failed',
                description: 'Failed to save equipment usage data. Please try again.'
            });
            // Stay in the same section on error - button will be re-enabled
        }
    };

    const handleNext = () => {
        if (isDirty) {
            Modal.confirm({
                title: 'Warning!',
                icon: <ExclamationCircleOutlined />,
                content: 'You have made changes to the table. Do you want to save them?',
                okText: 'Save',
                cancelText: 'Discard',
                onOk: () => {
                    handleSave();
                    if (onComplete) onComplete(!skipped);
                },
                onCancel: () => {
                    if (onComplete) onComplete(!skipped);
                }
            });
        } else {
            if (onComplete) onComplete(!skipped);
        }
    };

    return (
        <div style={{ padding: 24, position: 'relative' }}>
            <Spin
                spinning={isEquipmentUsageLogLoading}
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                tip="Loading equipment usage data..."
                size="large"
            >
                {isShiftSkipped ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        backgroundColor: 'rgba(250, 173, 20, 0.1)',
                        border: '1px solid #faad14',
                        borderRadius: 8,
                        marginBottom: 24
                    }}>
                        <div style={{
                            fontSize: 18,
                            fontWeight: 600,
                            color: '#faad14',
                            marginBottom: 8
                        }}>
                            {shift.charAt(0).toUpperCase() + shift.slice(1)} Shift Skipped
                        </div>
                        <div style={{
                            fontSize: 14,
                            color: '#666'
                        }}>
                            This section is disabled because the {shift} shift has been skipped.
                        </div>
                    </div>
                ) : (
                    <>
                        <div style={{ marginBottom: 16, height: '40vh', overflow: 'auto' }}>
                            <AgGridTable
                                rowData={rowData}
                                    columnDefs={columnDefs.map(col => ({ ...col, editable: col.editable === false ? false : !skipped && !isSubmitted }))}
                                    onCellValueChanged={onCellValueChanged}
                                    theme="ag-theme-material-dark"
                                    loading={isEquipmentUsageLogLoading}
                                />
                            </div>

                            <div style={{ marginBottom: 24 }}>
                            <Title level={5} style={{ textAlign: 'left' }}>Comments</Title>
                            <TextArea
                                rows={3}
                                placeholder="Please add your comments here"
                                value={comments}
                                onChange={handleCommentsChange}
                                disabled={skipped || isSubmitted}
                            />
                        </div>
                    </>
                )}
            </Spin>

            {/* Fixed Save/Cancel Buttons */}
            <div style={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                zIndex: 1000,
                display: 'flex',
                gap: 12
            }}>
                <Button
                    onClick={onCancel}
                    size="large"
                    shape="round"
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    type="primary"
                    size="large"
                    shape="round"
                    disabled={isUpdatingEquipmentUsageLog || isSubmitted || isShiftSkipped}
                    loading={isUpdatingEquipmentUsageLog}
                >
                    Save & Next
                </Button>
            </div>
        </div>
    );
};

const mapStateToProps = ({ dsrReducer }) => ({
    isEquipmentUsageLogLoading: dsrReducer.isEquipmentUsageLogLoading,
    isUpdatingEquipmentUsageLog: dsrReducer.isUpdatingEquipmentUsageLog,
    currentEquipmentUsageLog: dsrReducer.currentEquipmentUsageLog,
});

const mapDispatchToProps = (dispatch) => ({
    onFetchEquipmentUsageLog: (payload) => dispatch(fetchEquipmentUsageLog(payload)),
    onUpdateEquipmentUsageLog: (payload) => dispatch(updateEquipmentUsageLog(payload)),
    onUpdateDsrLogSections: (payload) => dispatch(updateDsrLogSections(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EquipmentUsageSection); 